import React, { useEffect, useState } from "react";
import PopupModules from "src/features/auth/components/PopupModules";
import InputBox from "../../components/InputBox";
import { Button } from "src/business-types/grocery/components/Button";
import { notifyError } from "src/assets/toastService";
import { Order } from "src/shared/types";

interface Props {
  payment: boolean;
  cancel: () => void;
  totalSOB: number;
  order: Order;
  callApi: (data: any) => void;
  payOnly: boolean;
  orderStatus: string;
}

function PaymentPopup({
  payment,
  cancel,
  totalSOB = 0,
  order,
  callApi,
  payOnly,
  orderStatus,
}: Props) {
  const [selectedOption, setSelectedOption] = useState<string>("Cash");
  const [resPrice, setResPrice] = useState<string>("");

  const now = new Date();
  const localDate =
    now.getFullYear() +
    "-" +
    String(now.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(now.getDate()).padStart(2, "0");

  useEffect(() => {
    setResPrice("");
  }, [payment]);

  if (!order) {
    return (
      <PopupModules isOpen={payment} onClose={cancel} title="Order Payment">
        <div className="p-4 text-center text-gray-500">
          Loading order details...
        </div>
      </PopupModules>
    );
  }

  // ── New interface field paths ─────────────────────────────────────────────
  const transaction = order.orders?.[0];
  const finalAmount = Number(transaction?.finalAmount ?? 0);
  const currentOrderStatus = transaction?.status ?? "";

  // receivedAmount is the correct field in new paymentHistory schema
  const alreadyPaid = (order.paymentHistory ?? []).reduce(
    (acc, item) => acc + Number(item?.receivedAmount ?? 0),
    0
  );

  // No adjustAmount in new schema
  const remaining = Math.max(Number(totalSOB) || 0, 0);
  const newBalance = Math.max(remaining - (Number(resPrice) || 0), 0);

  // returnHistory not in new schema — default 0
  const orderReturnAmount =
    (order as any)?.returnHistory?.reduce((acc: number, p: any) => {
      return (
        acc +
        (p.items?.reduce(
          (s: number, i: any) => s + Number(i.netAmount ?? 0),
          0
        ) ?? 0)
      );
    }, 0) ?? 0;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const radioBtn = (title: string) => (
    <div
      key={title}
      className={`px-2 py-1 flex gap-2 items-center cursor-pointer border rounded-md ${selectedOption === title
        ? "bg-blue-100 border-blue-500"
        : "border-gray-300"
        }`}
      onClick={() => setSelectedOption(title)}
    >
      <input
        type="radio"
        name="payment"
        className="w-5 h-5"
        checked={selectedOption === title}
        onChange={() => setSelectedOption(title)}
      />
      <p className="capitalize">{title}</p>
    </div>
  );

  const pricePayment = (value: string) => {
    const decimalRegex = /^\d+(\.\d{0,2})?$/;
    if (!decimalRegex.test(value)) {
      if (value !== "") console.warn("Only up to 2 decimal places allowed.");
      else setResPrice("");
      return;
    }
    const number = Number(value);
    if (!isNaN(number) && number >= 0 && number <= remaining) {
      setResPrice(value);
    }
  };

  const paymentProcess = () => {
    const amount = Number(resPrice);
    if (totalSOB <= 0 && remaining <= 0) {
      notifyError("Outstanding balance already cleared.");
      setResPrice("");
      return;
    }

    // const orderRemaining = (finalAmount - alreadyPaid - amount)
    // const updateRemaining = orderRemaining >= 0 ? orderRemaining : 0;

    const orderRemaining = Math.max(remaining - amount, 0);
    const updateRemaining = orderRemaining >= 0 ? orderRemaining : 0;

    const basePayment = {
      date: localDate,
      time: now.toTimeString().split(" ")[0],
      customerId: transaction?.customerId,
      outstandingBalance: Number(totalSOB),
      remainingAmount: updateRemaining,
      orderStatus: orderStatus,
      payments:
        { paymentMethod: selectedOption, receivedAmount: amount, amountDue: updateRemaining },

    };

    const fullPayment = {
      outstandingBalance: Number(totalSOB),
      status: currentOrderStatus,
      status_change: currentOrderStatus === orderStatus ? false : true,
      date: localDate,
      time: now.toTimeString().split(" ")[0],
      remainingAmount: updateRemaining,
      // deliveryDate: localDate,
      // deliveryTime: now.toTimeString().split(" ")[0],
      payments: { paymentMethod: selectedOption, receivedAmount: amount, amountDue: updateRemaining },
    };

    setResPrice("");
    callApi(payOnly ? basePayment : fullPayment);
  };

  return (
    <PopupModules
      isOpen={payment}
      onClose={() => {
        cancel();
        setResPrice("");
      }}
      title="Order Payment"
    >
      <div className="pb-2">
        {/* Payment Type */}
        <div className="bg-gray-100 rounded-lg p-4 mb-4">
          <h2 className="text-lg font-medium text-gray-700 mb-2">
            Choose Payment Type
          </h2>
          <div className="flex gap-4 flex-wrap justify-center">
            {radioBtn("Card")}
            {radioBtn("Cash")}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-gray-100 rounded-lg p-4">
          <h2 className="text-lg font-medium text-gray-700 mb-2">
            Payment Summary
          </h2>
          <div className="grid grid-cols-2 gap-y-2 text-sm text-gray-700 px-2">
            <p className="font-medium">Total OSB:</p>
            <p>{Number(totalSOB).toFixed(2)}</p>

            <p className="font-medium">Order Price:</p>
            <p>{finalAmount.toFixed(2)}</p>

            <p className="font-medium">Return Amount (-):</p>
            <p>{orderReturnAmount.toFixed(2)}</p>

            <p className="font-medium">Already Paid:</p>
            <p>{alreadyPaid.toFixed(2)}</p>

            <p className="font-medium">Remaining Balance:</p>
            <p>{remaining.toFixed(2)}</p>

            <p className="font-medium">Enter Payment:</p>
            <InputBox
              disabled={selectedOption === "Buy on credit"}
              min={0}
              type="text"
              onChange={(e) => pricePayment(e.target.value)}
              value={resPrice}
            />

            <p className="font-medium">New Balance:</p>
            <p>{newBalance.toFixed(2)}</p>
          </div>
        </div>

        <div className="flex justify-center gap-4 mt-6">
          <Button
            label="Cancel"
            variant="cancel"
            onClick={() => {
              cancel();
              setResPrice("");
            }}
          />
          <Button
            label="Submit Payment"
            onClick={paymentProcess}
            disabled={
              selectedOption !== "Buy on credit" && Number(resPrice) === 0
            }
          />
        </div>
      </div>
    </PopupModules>
  );
}

export default PaymentPopup;